import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import MarketingHeader from './components/MarketingHeader';
import PricingCards from './components/PricingCards';
import LegalFooter from './components/LegalFooter';
import useDocumentMeta from './hooks/useDocumentMeta';
import useRevealObserver from './hooks/useRevealObserver';
import useTheme from './hooks/useTheme';
import { REQUEST_ACCESS_URL } from './config/links';
import Walk from './components/Walk';
import HeroDemo from './components/HeroDemo';
import MilTabs from './components/MilTabs';
import ProductTree from './components/ProductTree';
import LoopSelector from './components/LoopSelector';
import { HERO, WHY, FLOW, MIL, FITS, CLOSE } from './content/narrative';

/* The landing — one responsive page served at / for every viewport:
   hero (the drive) → walk (DRV-400, idea to Rev B) → why Rev B is better →
   the seven-step cycle with the product tree → MIL → where it fits →
   pricing → close. Above the fold sells agency; below it, the record. */

const serif = "Georgia, 'Times New Roman', serif";
const sans = "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const mono = "ui-monospace, 'SF Mono', 'Cascadia Code', Menlo, Consolas, monospace";

const Kicker = ({ children }) => <div className="wb-kicker">{children}</div>;

const H2 = ({ children, style }) => (
  <h2 className="wb-display" style={{ fontSize: 'var(--t-lg)', margin: '14px 0 20px', ...style }}>{children}</h2>
);

const Section = ({ id, children, className = '', style }) => (
  <section id={id} className={`px-5 py-20 md:py-[104px] ${className}`} style={{ borderTop: '1px solid var(--wb-line)', ...style }}>
    <div className="max-w-[1180px] mx-auto">{children}</div>
  </section>
);

const Lead = ({ children, style }) => (
  <p style={{ fontSize: 'var(--t-md)', lineHeight: 1.6, color: 'var(--wb-muted)', maxWidth: 720, fontFamily: sans, margin: 0, ...style }}>{children}</p>
);

const FactoryOSLanding = () => {
  const [searchParams] = useSearchParams();

  useDocumentMeta({
    title: 'Factory OS: self-driving for hardware engineering',
    description:
      'Describe what you’re building. Factory OS walks requirements, design, analysis and test with you approving each step, and keeps the why for Rev B.',
    path: '/',
  });

  useRevealObserver();

  useEffect(() => {
    const voucherParam = searchParams.get('voucher');
    const joinParam = searchParams.get('join');
    if (voucherParam || joinParam !== null) {
      window.location.replace(
        REQUEST_ACCESS_URL + (voucherParam ? `?voucher=${encodeURIComponent(voucherParam)}` : '')
      );
    }
  }, [searchParams]);

  const [theme, toggleTheme] = useTheme();

  const Ctas = ({ className = '' }) => (
    <div className={`flex flex-wrap gap-3 justify-center ${className}`}>
      <a className="wb-btn" href={REQUEST_ACCESS_URL}>{HERO.ctaPrimary}</a>
      {HERO.watchUrl && <a className="wb-btn ghost" href={HERO.watchUrl}>▶ Watch the loop</a>}
    </div>
  );

  return (
    <div className="fos-workbench min-h-screen">
      <MarketingHeader theme={theme} onToggleTheme={toggleTheme} />

      {/* ══════════ 1 · HERO — one line, one sentence, then the product ══════════ */}
      <section className="px-5 pt-10 sm:pt-16 pb-14">
        <div className="max-w-[1180px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.05fr] gap-10 lg:gap-14 items-center">
          <div className="text-center lg:text-left">
            <h1 className="wb-display wb-ink-in" style={{ fontSize: 'var(--t-lg)', lineHeight: 1.04, letterSpacing: '-0.03em', margin: 0 }}>
              {HERO.h1}
            </h1>
            <p className="wb-ink-in" style={{ fontSize: 'var(--t-md)', lineHeight: 1.6, color: 'var(--wb-muted)', maxWidth: 520, margin: '20px 0 0', animationDelay: '0.12s', fontFamily: sans }}>
              {HERO.sub}
            </p>
            <div className="flex flex-wrap gap-3 mt-7 justify-center lg:justify-start wb-ink-in" style={{ animationDelay: '0.24s' }}>
              <a className="wb-btn" href={REQUEST_ACCESS_URL}>{HERO.ctaPrimary}</a>
              <a className="wb-btn ghost" href="#walk">{HERO.ctaSecondary} ↓</a>
            </div>
            <p className="wb-ink-in" style={{ marginTop: 14, color: 'var(--wb-muted)', fontSize: 'var(--t-sm)', letterSpacing: '0.04em', animationDelay: '0.34s', fontFamily: sans }}>
              {HERO.audience}
            </p>
          </div>
          <div className="wb-ink-in" style={{ animationDelay: '0.3s' }}>
            <HeroDemo />
          </div>
        </div>
      </section>

      {/* ══════════ 2 · THE WALK — the DRV-400 drive, idea to Rev B ══════════ */}
      <Walk />

      {/* ══════════ 3 · WHY REV B IS BETTER — drive hands off to the record ══════════ */}
      <Section id="why">
        <div className="reveal-hidden" data-reveal>
          <Kicker>{WHY.eyebrow}</Kicker>
          <H2 style={{ maxWidth: 820 }}>
            {WHY.headline[0]}{' '}
            <em style={{ color: 'var(--wb-orange)', display: 'block' }}>{WHY.headline[1]}</em>
          </H2>
        </div>
        {/* The cadence: the loop drives → every step writes a trace → the next revision reads it. */}
        <ol className="list-none m-0 p-0 mt-2 grid grid-cols-1 md:grid-cols-3 gap-3 reveal-hidden" data-reveal style={{ transitionDelay: '80ms' }}>
          {WHY.cadence.map((c, i) => (
            <li key={c} className="flex items-center gap-3" style={{ padding: '16px 18px', background: 'var(--wb-paper)', border: '1px solid var(--wb-line)', borderRadius: 12 }}>
              <span style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-orange)', fontWeight: 800 }}>{String(i + 1).padStart(2, '0')}</span>
              <span style={{ fontFamily: sans, fontWeight: 700, fontSize: 'var(--t-md)', color: 'var(--wb-ink)' }}>{c}</span>
            </li>
          ))}
        </ol>
        <div className="reveal-hidden" data-reveal style={{ transitionDelay: '140ms' }}>
          <p style={{ fontFamily: serif, fontStyle: 'italic', fontSize: 'var(--t-md)', color: 'var(--wb-ink)', maxWidth: 720, lineHeight: 1.5, margin: '22px 0 0' }}>{WHY.line}</p>
          <Lead style={{ marginTop: 12 }}>{WHY.proof}</Lead>
          <p style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-muted)', letterSpacing: '0.04em', margin: '14px 0 0' }}>{WHY.audience}</p>
        </div>
      </Section>

      {/* ══════════ 4 · THE SEVEN-STEP CYCLE — captured as a thread; every node runs it ══════════ */}
      <Section id="how">
        <div className="reveal-hidden" data-reveal>
          <Kicker>{FLOW.eyebrow}</Kicker>
          <H2 style={{ maxWidth: 900 }}>{FLOW.headline}</H2>
          <Lead>{FLOW.sub}</Lead>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-8 items-start mt-10">
          <div className="reveal-hidden" data-reveal style={{ transitionDelay: '120ms' }}>
            <LoopSelector />
            <p style={{ marginTop: 18, fontSize: 'var(--t-md)', color: 'var(--wb-muted)', fontFamily: sans, lineHeight: 1.6 }}>{FLOW.close}</p>
          </div>
          <div className="reveal-hidden" data-reveal style={{ transitionDelay: '180ms' }}>
            <ProductTree />
          </div>
        </div>
      </Section>

      {/* ══════════ 5 · MIL — self-driving, not a black box ══════════ */}
      <Section id="mil">
        <div className="reveal-hidden" data-reveal>
          <Kicker>{MIL.eyebrow}</Kicker>
          <H2 style={{ maxWidth: 760 }}>{MIL.headline}</H2>
          <Lead>{MIL.line}</Lead>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.15fr] gap-8 md:gap-12 items-start mt-8">
          <ul className="list-none p-0 m-0 reveal-hidden" data-reveal style={{ display: 'grid', gap: 14 }}>
            {MIL.points.map((pt) => (
              <li key={pt} className="flex gap-3" style={{ fontFamily: sans, fontSize: 'var(--t-md)', color: 'var(--wb-ink)', lineHeight: 1.55 }}>
                <span aria-hidden style={{ fontFamily: mono, color: 'var(--wb-orange)', flex: '0 0 auto' }}>→</span>
                <span>{pt}</span>
              </li>
            ))}
          </ul>
          <div className="reveal-hidden" data-reveal style={{ transitionDelay: '120ms' }}>
            <MilTabs />
          </div>
        </div>
      </Section>

      {/* ══════════ 6 · WHERE FACTORY OS FITS ══════════ */}
      <Section id="fits">
        <div className="reveal-hidden" data-reveal>
          <Kicker>{FITS.eyebrow}</Kicker>
          <H2 style={{ maxWidth: 820 }}>{FITS.headline}</H2>
        </div>
        <div className="mt-8 reveal-hidden" data-reveal style={{ transitionDelay: '120ms', borderTop: '1px solid var(--wb-line)' }}>
          {FITS.rows.map(([k, v]) => (
            <div key={k} className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-1 md:gap-6" style={{ padding: '16px 2px', borderBottom: '1px solid var(--wb-line)' }}>
              <div style={{ fontFamily: mono, fontSize: 'var(--t-sm)', fontWeight: 800, color: 'var(--wb-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', paddingTop: 3 }}>{k}</div>
              <p style={{ fontFamily: sans, fontSize: 'var(--t-md)', color: 'var(--wb-ink)', margin: 0, lineHeight: 1.55, maxWidth: 760 }}>{v}</p>
            </div>
          ))}
          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-1 md:gap-6" style={{ padding: '18px 16px', marginTop: 12, background: 'var(--wb-paper)', border: '1px solid var(--wb-orange)', borderRadius: 14 }}>
            <div style={{ fontFamily: mono, fontSize: 'var(--t-sm)', fontWeight: 800, color: 'var(--wb-orange)', letterSpacing: '0.08em', textTransform: 'uppercase', paddingTop: 3 }}>{FITS.ours[0]}</div>
            <p style={{ fontFamily: sans, fontWeight: 700, fontSize: 'var(--t-md)', color: 'var(--wb-ink)', margin: 0, lineHeight: 1.55, maxWidth: 760 }}>{FITS.ours[1]}</p>
          </div>
        </div>
      </Section>

      {/* ══════════ 7 · PRICING (unchanged) ══════════ */}
      <Section id="pricing">
        <div className="reveal-hidden" data-reveal>
          <Kicker>pricing</Kicker>
          <H2>Your first 100 nodes are free.</H2>
          <Lead>Start free — no card, no timer.</Lead>
        </div>
        <div className="mt-12 reveal-hidden" data-reveal style={{ transitionDelay: '120ms' }}>
          <PricingCards />
        </div>
      </Section>

      {/* ══════════ 8 · CLOSE — the category line and the primary CTA ══════════ */}
      <section className="px-5 py-24 md:py-[120px] text-center" style={{ borderTop: '1px solid var(--wb-line)' }}>
        <div className="max-w-[860px] mx-auto reveal-hidden" data-reveal>
          <H2 style={{ fontSize: 'var(--t-lg)' }}>{CLOSE.headline}</H2>
          <Lead style={{ margin: '0 auto' }}>{CLOSE.body}</Lead>
          <Ctas className="mt-8" />
        </div>
      </section>

      <LegalFooter />
    </div>
  );
};

export default FactoryOSLanding;
